/**
 * QRScanner Component - Reusable QR code scanner for Rapid X
 * 
 * Used for:
 * - Site check-in via QR
 * - Patrol checkpoint scanning
 * - Shift verification
 */
import React, { useEffect, useRef, useState } from 'react';
import { Html5QrcodeScanner, Html5QrcodeScanType } from 'html5-qrcode';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { QrCode, Camera, X, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';

export function QRScanner({ 
  open, 
  onOpenChange, 
  onScanSuccess, 
  onScanError,
  title = "Scan QR Code",
  description = "Position the QR code within the camera view"
}) {
  const scannerRef = useRef(null);
  const [scannerInstance, setScannerInstance] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState(null);
  const [lastResult, setLastResult] = useState(null);

  useEffect(() => {
    if (open && !scannerInstance) {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        initScanner();
      }, 100);
      return () => clearTimeout(timer);
    }
    
    return () => {
      if (scannerInstance) {
        try {
          scannerInstance.clear();
        } catch (e) {
          console.log('Scanner cleanup error:', e);
        }
      }
    };
  }, [open]);

  const initScanner = () => {
    try {
      const config = {
        fps: 10,
        qrbox: { width: 250, height: 250 },
        aspectRatio: 1.0,
        supportedScanTypes: [Html5QrcodeScanType.SCAN_TYPE_CAMERA]
      };

      const scanner = new Html5QrcodeScanner("qr-reader", config, false);
      
      scanner.render(
        (decodedText) => {
          setLastResult(decodedText);
          setScanning(false);
          if (onScanSuccess) {
            onScanSuccess(decodedText);
          }
        },
        (errorMessage) => {
          // Ignore continuous scanning errors, only show critical ones
          if (errorMessage.includes('NotFound') || errorMessage.includes('Permission')) {
            setError(errorMessage);
          }
        }
      );

      setScannerInstance(scanner);
      setScanning(true);
      setError(null);
    } catch (e) {
      setError('Failed to initialize camera. Please check permissions.');
      console.error('Scanner init error:', e);
    }
  };

  const handleClose = () => {
    if (scannerInstance) {
      try {
        scannerInstance.clear();
      } catch (e) {
        console.log('Scanner cleanup error:', e);
      }
      setScannerInstance(null);
    }
    setScanning(false);
    setError(null);
    setLastResult(null);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-[#18181b] border-[#3f3f46] text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <QrCode className="w-5 h-5 text-blue-400" />
            {title}
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            {description}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Scanner container */}
          <div 
            id="qr-reader" 
            ref={scannerRef}
            className="w-full rounded-lg overflow-hidden bg-black"
            style={{ minHeight: '300px' }}
          />

          {/* Status indicators */}
          {scanning && !error && (
            <div className="flex items-center justify-center gap-2 text-blue-400">
              <Camera className="w-4 h-4 animate-pulse" />
              <span className="text-sm">Scanning for QR code...</span>
            </div>
          )}

          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-md">
              <div className="flex items-center gap-2 text-red-400">
                <AlertCircle className="w-4 h-4" />
                <span className="text-sm">{error}</span>
              </div>
            </div>
          )}

          {lastResult && (
            <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-md">
              <div className="flex items-center gap-2 text-emerald-400">
                <CheckCircle2 className="w-4 h-4" />
                <span className="text-sm">QR Code detected!</span>
              </div>
            </div>
          )}

          {/* Instructions */}
          <div className="text-center text-xs text-zinc-500">
            <p>Hold your device steady and align the QR code within the frame</p>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <Button 
            variant="outline" 
            onClick={handleClose}
            className="border-zinc-700"
          >
            <X className="w-4 h-4 mr-2" />
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * QRDisplay Component - Display a QR code for sites/checkpoints
 */
export function QRDisplay({ 
  value, 
  size = 200, 
  title = "QR Code",
  description = "Scan this code to verify"
}) {
  const QRCode = require('qrcode.react').QRCodeSVG;
  
  return (
    <div className="flex flex-col items-center space-y-3 p-4 bg-white rounded-lg">
      <QRCode 
        value={value} 
        size={size}
        level="M"
        includeMargin={true}
      />
      <div className="text-center">
        <p className="text-zinc-800 font-medium text-sm">{title}</p>
        <p className="text-zinc-500 text-xs">{description}</p>
      </div>
    </div>
  );
}

export default QRScanner;
